import React, { useRef, useState } from 'react';
import type { UpdateTimeEntryDto } from 'api';
import type { DetailedEntry } from '../utils/reportsUtils';
import {
  DetailedDescription,
  DetailedDescriptionInput,
  DetailedProjectName,
  DetailedClientName,
  DetailedTitleCell,
  DetailedProjectClientRow,
} from '../reports.styles';

interface EditableTitleCellProps {
  entry: DetailedEntry;
  onUpdate?: (id: string, data: UpdateTimeEntryDto) => void;
}

const EditableTitleCellInner: React.FC<EditableTitleCellProps> = ({ entry, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.description ?? '');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync draft when entry changes from server
  React.useEffect(() => {
    if (!editing) setDraft(entry.description ?? '');
  }, [entry.description, editing]);

  const save = () => {
    setEditing(false);
    const trimmed = draft.trim();
    const original = (entry.description || '').trim();
    if (trimmed !== original && entry.projectId) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        onUpdate?.(entry.id, {
          projectId: entry.projectId as string,
          description: trimmed || undefined,
          startedAt: entry.startedAt,
          endedAt: entry.endedAt || undefined,
          isBillable: entry.isBillable,
        });
      }, 300);
    }
  };

  return (
    <DetailedTitleCell>
      {editing ? (
        <DetailedDescriptionInput
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          variant="standard"
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') {
              setDraft(entry.description ?? '');
              setEditing(false);
            }
          }}
          placeholder="Add description"
        />
      ) : (
        <>
          <DetailedDescription onClick={onUpdate ? () => setEditing(true) : undefined}>
            {entry.description || '(no description)'}
          </DetailedDescription>
        </>
      )}
      <DetailedProjectClientRow>
        <DetailedProjectName color={entry.projectColor}>{entry.projectName}</DetailedProjectName>
        {entry.clientName && <DetailedClientName> - {entry.clientName}</DetailedClientName>}
      </DetailedProjectClientRow>
    </DetailedTitleCell>
  );
};

export const EditableTitleCell = React.memo(EditableTitleCellInner);
